/**
 * Row actions configuration for the audit logs table.
 */

import type { AuditLogEntry, AuditLogFilters } from '../../types';
import { type FilterValue, buildFiltersFromValues } from './filter-utils';
import type { Account } from '@/services/auth/types';
import { appendMasqParam } from '@/utils/masq';
import { getAuditLogDayLimit } from '../../utils/access';

interface CreateRowActionsOptions {
  filterValues: FilterValue[];
  setFilterValues: React.Dispatch<React.SetStateAction<FilterValue[]>>;
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  context: 'account' | 'workspace';
  contextId: number;
  account: Account;
  onShowRaw: (log: AuditLogEntry) => void;
  onShowMetadata: (log: AuditLogEntry) => void;
  onShowDiff: (log: AuditLogEntry) => void;
}

export function createRowActions({
  filterValues,
  setFilterValues,
  onFiltersChange,
  context,
  contextId,
  account,
  onShowRaw,
  onShowMetadata,
  onShowDiff,
}: CreateRowActionsOptions) {
  const dayLimit = getAuditLogDayLimit(account);
  return (row: { original: AuditLogEntry }) => {
    const log = row.original;
    return {
      dropdownTrigger: {
        tooltip: 'Actions',
        ariaLabel: 'Row actions',
      },
      dropdownActions: [
        {
          label: 'Filter for related changes',
          icon: 'object' as const,
          onClick: () => {
            // Construct combined object_type:action value
            const combinedValue = `${log.object_type}:${log.action.toLowerCase()}`;

            // Compute new filter values
            // Clear object_type, object_id, attribute_name, and campaign_id (campaign_id is only valid for CampaignAction)
            const newValues = filterValues.filter(
              (f) =>
                f.key !== 'object_type' &&
                f.key !== 'object_id' &&
                f.key !== 'attribute_name' &&
                f.key !== 'campaign_id',
            );
            newValues.push({ key: 'object_type', value: combinedValue });
            newValues.push({ key: 'object_id', value: log.object_id });

            // Update state and apply filters
            setFilterValues(newValues);
            const newFilters = buildFiltersFromValues(newValues, dayLimit);
            // Explicitly clear dependent filters from URL (nuqs needs undefined to remove params)
            onFiltersChange({ ...newFilters, attribute_name: undefined, campaign_id: undefined });
          },
        },
        {
          label: 'Copy link',
          icon: 'link' as const,
          onClick: async () => {
            const baseUrl = window.location.origin;
            const path =
              context === 'account'
                ? appendMasqParam(`/settings/audit-logs?audit_log_id=${log.id}`)
                : `/workspaces/${contextId}/settings/audit-logs?audit_log_id=${log.id}`;
            const url = `${baseUrl}${path}`;
            try {
              await navigator.clipboard.writeText(url);
            } catch {
              // Fallback for older browsers
              try {
                const textArea = document.createElement('textarea');
                textArea.value = url;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
              } catch {
                // Clipboard copy failed silently
              }
            }
          },
        },
        {
          label: 'View raw',
          icon: 'editor-code' as const,
          onClick: () => {
            onShowRaw(log);
          },
        },
        {
          label: 'Show metadata',
          icon: 'info' as const,
          onClick: () => {
            onShowMetadata(log);
          },
        },
        {
          label: 'Show diff',
          icon: 'inspect' as const,
          onClick: () => {
            onShowDiff(log);
          },
        },
      ],
    };
  };
}
